import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import { exportExcelSection, exportPDFSection } from '../common/ExportUtils';
import { getAuthHeaders } from '../../auth';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext';
import '../documentation/JobList.css';
import { openFilePreview } from '../common/FilePreviewModal';
import TableScrollContainer from '../common/TableScrollContainer';

const API_BASE = '/api/documentation/jobs';

const fundRequestIcon = (r, released) => r.is_fund_request && !r.fund_request_released && !released && (r.znz_remarks || '').toUpperCase() !== 'RELEASED' ? (
  <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginRight: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />
) : null;

const CopyCell = ({ text, children }) => {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = (e) => {
        e.stopPropagation();
        const val = text != null ? String(text) : (typeof children === 'string' ? children : '');
        if (!val || val === '-') return;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(val).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
            });
        } else {
            const ta = document.createElement('textarea');
            ta.value = val;
            ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {}
            document.body.removeChild(ta);
        }
    };
    return (
        <span className="copy-cell">
            {children}
            <button
                type="button"
                className={`copy-icon-btn${copied ? ' copied' : ''}`}
                title={copied ? 'Copied!' : 'Copy'}
                onClick={handleCopy}
                onMouseDown={e => e.stopPropagation()}
            >
                {copied
                    ? <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    : <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                }
            </button>
        </span>
    );
};

const RELEASE_ORDER_OPTIONS = [
  { id: 'OBTAINED', text: 'OBTAINED' },
  { id: 'NOT OBTAINED', text: 'NOT OBTAINED' },
  { id: 'N/A', text: 'N/A' },
];

const toUpperDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value.toString().toUpperCase();
};

const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const n = Number(value);
    if (Number.isNaN(n)) return value.toString().toUpperCase();
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ZNZMainTab = ({ fixedRemarkFilter = '', refreshKey }) => {
  const { hasPermission } = usePermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [idfFilter, setIdfFilter] = useState('');
  const [search, setSearch] = useState('');
  const [remarkFilter, setRemarkFilter] = useState(fixedRemarkFilter || '');
  const [dropdowns, setDropdowns] = useState({ remarks: [] });
  const [savingId, setSavingId] = useState(null);
  const [editingCells, setEditingCells] = useState({});
  const [drafts, setDrafts] = useState({});
  const [dragSrcId, setDragSrcId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const isReleasedReport = (fixedRemarkFilter || '').toUpperCase() === 'RELEASED';

  const fetchDropdowns = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/znz-dropdowns/`, { headers: getAuthHeaders(false) });
      if (!res.ok) return;
      const data = await res.json();
      setDropdowns({
        remarks: data.remarks || [],
        release_order_options: data.release_order_options || RELEASE_ORDER_OPTIONS,
      });
    } catch {
      // Non-blocking fetch.
    }
  }, []);

  const fetchRowsForRemarkFilter = useCallback(async (nextRemarkFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (nextRemarkFilter) params.append('remark_filter', nextRemarkFilter);
      const res = await fetch(`${API_BASE}/znz-report/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch ZNZ report.');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || 'Failed to fetch ZNZ report.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    await fetchRowsForRemarkFilter(remarkFilter);
  }, [fetchRowsForRemarkFilter, remarkFilter]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    if (refreshKey !== undefined) {
      fetchRows();
    }
  }, [refreshKey, fetchRows]);

  useEffect(() => {
    setDrafts(prev => {
      const next = { ...prev };
      rows.forEach(r => {
        next[r.id] = {
          ...(next[r.id] || {}),
          eta: r.eta || '',
          releaseOrder: r.release_order || '',
          releaseOrderFile: null,
          remarks: r.raw_remarks || '',
          releaseDate: r.release_date || '',
          dutyPaid: r.duty_paid || false,
        };
      });
      return next;
    });
  }, [rows]);

  const getCellKey = (rowId, field) => `${rowId}:${field}`;

  const isEditingCell = (rowId, field) => !!editingCells[getCellKey(rowId, field)];

  const startEditCell = (row, field) => {
    setDrafts(prev => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] || {}),
        eta: row.eta || '',
        releaseOrder: row.release_order || '',
        releaseOrderFile: null,
        remarks: row.raw_remarks || '',
        releaseDate: row.release_date || '',
        dutyPaid: row.duty_paid || false,
      },
    }));
    setEditingCells(prev => ({ ...prev, [getCellKey(row.id, field)]: true }));
  };

  const cancelEditCell = (row, field) => {
    setDrafts(prev => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] || {}),
        eta: row.eta || '',
        releaseOrder: row.release_order || '',
        releaseOrderFile: null,
        remarks: row.raw_remarks || '',
        releaseDate: row.release_date || '',
        dutyPaid: row.duty_paid || false,
      },
    }));
    setEditingCells(prev => {
      const next = { ...prev };
      delete next[getCellKey(row.id, field)];
      return next;
    });
  };

  const saveEditCell = async (row, field, payload) => {
    if (!hasPermission('ZNZ REPORT', 'can_edit')) {
      toast.error('You do not have permission to edit the ZNZ report.');
      return false;
    }
    setSavingId(row.id);
    try {
      const res = await fetch(`${API_BASE}/${row.id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to update record.');
      await fetchRows();
      await fetchDropdowns();
      setEditingCells(prev => {
        const next = { ...prev };
        delete next[getCellKey(row.id, field)];
        return next;
      });
      return true;
    } catch (e) {
      toast.error(e.message || 'Failed to update record.');
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveReleaseOrder = async (row) => {
    if (!hasPermission('ZNZ REPORT', 'can_edit')) {
      toast.error('You do not have permission to edit the ZNZ report.');
      return;
    }
    const draft = drafts[row.id] || {};
    const value = (draft.releaseOrder || '').toUpperCase();
    const existingDoc = row.release_order_document;
    if (value === 'OBTAINED' && !existingDoc && !draft.releaseOrderFile) {
      toast.error('Please upload a Release Order document.');
      return;
    }
    setSavingId(row.id);
    try {
      let uploadResult = existingDoc || null;
      if (value === 'OBTAINED' && draft.releaseOrderFile) {
        const formData = new FormData();
        formData.append('document_type', 'Release Order');
        formData.append('document_file', draft.releaseOrderFile);
        const res = await fetch(`${API_BASE}/${row.id}/upload-job-document/`, {
          method: 'POST',
          headers: getAuthHeaders(true),
          body: formData,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to upload Release Order document.');
        uploadResult = data;
      }
      const patchRes = await fetch(`${API_BASE}/${row.id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ border_release_order: draft.releaseOrder || null }),
      });
      if (!patchRes.ok) throw new Error('Failed to update release order.');
      await fetchRows();
      await fetchDropdowns();
      setEditingCells(prev => {
        const next = { ...prev };
        delete next[getCellKey(row.id, 'releaseOrder')];
        return next;
      });
    } catch (e) {
      toast.error(e.message || 'Failed to save release order.');
    } finally {
      setSavingId(null);
    }
  };

  const handleDragStart = useCallback((e, jobId) => {
    setDragSrcId(jobId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, jobId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (jobId !== dragSrcId) setDragOverId(jobId);
  }, [dragSrcId]);

  const handleDragEnd = useCallback(() => {
    setDragSrcId(null);
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback(async (e, targetId) => {
    e.preventDefault();
    if (!dragSrcId || dragSrcId === targetId) { handleDragEnd(); return; }
    const newRows = [...rows];
    const srcIdx = newRows.findIndex(r => r.id === dragSrcId);
    const tgtIdx = newRows.findIndex(r => r.id === targetId);
    if (srcIdx === -1 || tgtIdx === -1) { handleDragEnd(); return; }
    const movingDown = srcIdx < tgtIdx;
    const [srcRow] = newRows.splice(srcIdx, 1);
    const newTgtIdx = newRows.findIndex(r => r.id === targetId);
    newRows.splice(movingDown ? newTgtIdx + 1 : newTgtIdx, 0, srcRow);
    setRows(newRows);
    handleDragEnd();
    const orders = newRows.map((r, i) => ({ id: r.id, sort_order: i }));
    try {
      const res = await fetch(`${API_BASE}/update-znz-sort-order/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ orders }),
      });
      if (!res.ok) throw new Error('Failed to save order.');
    } catch {
      toast.error('Failed to save row order.');
      fetchRows();
    }
  }, [dragSrcId, rows, setRows, handleDragEnd, fetchRows]);

  const clearAllFilters = async () => {
    setIdfFilter('');
    setSearch('');
    const nextRemark = fixedRemarkFilter || '';
    setRemarkFilter(nextRemark);
    await fetchRowsForRemarkFilter(nextRemark);
  };

  const filteredRows = useMemo(() => {
    let data = [...rows];
    if (idfFilter.trim()) {
      const needle = idfFilter.trim().toUpperCase();
      data = data.filter(r => (r.idf_no || '').toUpperCase().includes(needle));
    }
    if (search.trim()) {
      const needle = search.trim().toUpperCase();
      data = data.filter(r =>
        [
          r.idf_no,
          r.tansad_no,
          r.eta,
          r.importer,
          r.release_order,
          r.remarks,
        ].some(field => field && field.toString().toUpperCase().includes(needle))
      );
    }
    return data;
  }, [rows, idfFilter, search]);

  const acceptedRows = filteredRows
    .filter(r => ['ACCEPTED', 'SENT'].includes((r.assessment_status || '').toUpperCase()));

  const pendingRows = filteredRows
    .filter(r => !['ACCEPTED', 'SENT'].includes((r.assessment_status || '').toUpperCase()));

  const remarkOptions = useMemo(() => {
    const values = dropdowns.remarks.map(r => (typeof r === 'string' ? r : r.text)).filter(Boolean);
    if (!values.includes('RELEASED')) values.push('RELEASED');
    return Array.from(new Set(values)).map(v => ({ id: v, text: v }));
  }, [dropdowns.remarks]);

  const getRemarkOptionsForRow = (row) => {
    const releaseOrderObtained = (row.release_order || '').toUpperCase() === 'OBTAINED';
    return remarkOptions.filter(opt => opt.id !== 'RELEASED' || releaseOrderObtained);
  };

  const getPendingSectionRemark = (r) => {
    const jobStatus = Array.isArray(r.job_status) ? r.job_status : [];
    if (jobStatus.some(s => String(s).toUpperCase() === 'COMPLETE')) {
      return r.assessment_status_label || r.assessment_status || '';
    }
    return r.status_remark || '';
  };

  const renderRows = (dataRows, options = {}) => {
    const { readOnly = false, pendingSection = false } = options;
    const colSpan = pendingSection ? 6 : 7;
    return (
      <TableScrollContainer>
        <table className="jobs-table" style={{ overflow: 'visible' }}>
          <thead>
            <tr>
              <th>IDF NO</th>
              <th>TANSAD NO</th>
              <th>PACKAGES</th>
              <th>WEIGHT</th>
              <th>IMPORTER</th>
              {!pendingSection && <th>RELEASE ORDER</th>}
              <th>REMARKS</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={colSpan} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
            {!loading && dataRows.length === 0 && (<tr><td colSpan={colSpan} className="no-data">No records found.</td></tr>)}
            {!loading && dataRows.map(r => (
              <tr
                key={r.id}
                draggable={hasPermission('ZNZ REPORT', 'can_edit')}
                onDragStart={hasPermission('ZNZ REPORT', 'can_edit') ? (e) => handleDragStart(e, r.id) : undefined}
                onDragOver={(e) => handleDragOver(e, r.id)}
                onDrop={(e) => handleDrop(e, r.id)}
                onDragEnd={handleDragEnd}
                style={{ cursor: hasPermission('ZNZ REPORT', 'can_edit') ? 'grab' : undefined }}
              >
                <td style={{ userSelect: 'text' }}><CopyCell text={r.idf_no}>{toUpperDisplay(r.idf_no)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.tansad_no}>{toUpperDisplay(r.tansad_no)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.packages}>{toUpperDisplay(r.packages)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.weight}>{formatNumber(r.weight)}</CopyCell></td>
                <td style={{ userSelect: 'text' }}><CopyCell text={r.importer}><span><strong>{toUpperDisplay(r.importer)}</strong>{r.is_fund_request && !r.fund_request_released && (r.znz_remarks || '').toUpperCase() !== 'RELEASED' && <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginLeft: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />}</span></CopyCell></td>
                {!pendingSection && (
                <td>
                  {!readOnly && hasPermission('ZNZ REPORT', 'can_edit') ? isEditingCell(r.id, 'releaseOrder') ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                      <Select2Dropdown
                        options={RELEASE_ORDER_OPTIONS}
                        value={drafts[r.id]?.releaseOrder || ''}
                        onChange={val => setDrafts(prev => ({
                          ...prev,
                          [r.id]: { ...(prev[r.id] || {}), releaseOrder: val || '', releaseOrderFile: null },
                        }))}
                        valueKey='id'
                        displayKey='text'
                        placeholder='Select'
                        clearable
                        style={{ minWidth: 170 }}
                        disabled={savingId === r.id}
                      />
                      {drafts[r.id]?.releaseOrder === 'OBTAINED' && (
                        <>
                          <input
                            type='file'
                            id={`release-order-file-${r.id}`}
                            accept='.pdf,.png,.jpg,.jpeg'
                            style={{ display: 'none' }}
                            onChange={e => {
                              const file = e.target.files?.[0] || null;
                              setDrafts(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), releaseOrderFile: file } }));
                            }}
                          />
                          <label htmlFor={`release-order-file-${r.id}`} className='icon-btn' title='Upload Release Order' style={{ cursor: 'pointer' }}>
                            <i className='fa fa-upload' />
                          </label>
                          {drafts[r.id]?.releaseOrderFile && (
                            <span style={{ fontSize: 11, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={drafts[r.id].releaseOrderFile.name}>{drafts[r.id].releaseOrderFile.name}</span>
                          )}
                        </>
                      )}
                      {r.release_order_document && (
                        <button type='button' className='icon-btn' title='View Release Order' onClick={() => openFilePreview(r.release_order_document.document_file_url, 'Release Order')}><i className='fa fa-eye' /></button>
                      )}
                      <button className='icon-btn save-btn' title='Save' disabled={savingId === r.id} onClick={() => handleSaveReleaseOrder(r)}><i className='fa fa-save' /></button>
                      <button className='icon-btn cancel-btn' title='Cancel' onClick={() => cancelEditCell(r, 'releaseOrder')}><i className='fa fa-times' /></button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CopyCell text={r.release_order}><span>{toUpperDisplay(r.release_order)}</span></CopyCell>
                      {r.release_order_document && (
                        <button type='button' className='icon-btn' title='View Release Order' onClick={() => openFilePreview(r.release_order_document.document_file_url, 'Release Order')}><i className='fa fa-eye' /></button>
                      )}
                      <button type='button' className='icon-btn' title='Edit Release Order' onClick={() => startEditCell(r, 'releaseOrder')}><i className='fa fa-pencil' /></button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CopyCell text={r.release_order}>{toUpperDisplay(r.release_order)}</CopyCell>
                      {r.release_order_document && (
                        <button type='button' className='icon-btn' title='View Release Order' onClick={() => openFilePreview(r.release_order_document.document_file_url, 'Release Order')}><i className='fa fa-eye' /></button>
                      )}
                    </div>
                  )}
                </td>
                )}
                <td style={{ minWidth: 220 }}>
                  {pendingSection ? (
                    <CopyCell text={getPendingSectionRemark(r)}><span style={{ whiteSpace: 'pre-wrap' }}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(getPendingSectionRemark(r))}</span></CopyCell>
                  ) : !readOnly && hasPermission('ZNZ REPORT', 'can_edit') ? isEditingCell(r.id, 'remarks') ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Select2Dropdown
                            options={getRemarkOptionsForRow(r)}
                            value={drafts[r.id]?.remarks || ''}
                            onChange={val => setDrafts(prev => ({
                              ...prev,
                              [r.id]: { ...(prev[r.id] || {}), remarks: val || '', releaseDate: val !== 'RELEASED' ? '' : (prev[r.id]?.releaseDate || '') },
                            }))}
                            valueKey="id"
                            displayKey="text"
                            placeholder="SELECT REMARK"
                            clearable
                            style={{ width: '100%' }}
                            disabled={savingId === r.id}
                          />
                        </div>
                        <button
                          className="icon-btn save-btn"
                          title="Save"
                          disabled={savingId === r.id}
                          onClick={() => {
                            const remark = drafts[r.id]?.remarks || '';
                            if (remark === 'RELEASED' && !(drafts[r.id]?.releaseDate)) {
                              toast.error('Please enter a Release Date before saving RELEASED.');
                              return;
                            }
                            if (remark === 'RELEASED' && r.is_fund_request && !r.fund_request_released) {
                              toast.error('Cannot Release. Awaiting Funds from Client');
                              return;
                            }
                            const payload = { znz_remarks: remark || null };
                            if (remark === 'RELEASED' && drafts[r.id]?.releaseDate) payload.release_date = drafts[r.id].releaseDate;
                            saveEditCell(r, 'remarks', payload);
                          }}
                        ><i className="fa fa-save" /></button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'remarks')}><i className="fa fa-times" /></button>
                      </div>
                      {(drafts[r.id]?.remarks || '') === 'RELEASED' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', marginBottom: 0 }}>Release Date <span style={{ color: 'red' }}>*</span></label>
                          <input
                            type="date"
                            className="form-control"
                            style={{ fontSize: 12, padding: '2px 6px', height: 'auto', width: 130 }}
                            value={drafts[r.id]?.releaseDate || ''}
                            onChange={e => setDrafts(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), releaseDate: e.target.value } }))}
                            required
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CopyCell text={r.remarks}><span style={{ whiteSpace: 'pre-wrap' }}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(r.remarks)}</span></CopyCell>
                      <button type="button" className="icon-btn" title="Edit Remarks" onClick={() => startEditCell(r, 'remarks')}><i className="fa fa-pencil" /></button>
                    </div>
                  ) : (
                    <CopyCell text={r.remarks}>{fundRequestIcon(r, isReleasedReport)}{toUpperDisplay(r.remarks)}</CopyCell>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollContainer>
    );
  };

  if (!hasPermission('ZNZ REPORT', 'can_view')) {
    return (
      <div className="permission-denied-container" style={{ padding: '20px' }}>
        <h2>Permission Denied</h2>
        <p>You do not have permission to view ZNZ Reports.</p>
      </div>
    );
  }

  return (
    <div className="job-list-container" style={{ overflowX: 'auto' }}>
      <div className="toolbar" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'nowrap', alignItems: 'center', overflowX: 'auto' }}>
          <input className="search-input" placeholder="IDF Search" value={idfFilter} onChange={e => setIdfFilter(e.target.value)} style={{ minWidth: 140 }} />
          <Select2Dropdown
            options={[{ id: '', text: 'All Remarks' }, ...remarkOptions]}
            value={remarkFilter}
            onChange={val => setRemarkFilter(val || '')}
            valueKey="id"
            displayKey="text"
            style={{ minWidth: 180 }}
            placeholder="Remarks Filter"
            isDisabled={!!fixedRemarkFilter}
          />
          <input className="search-input" placeholder="General Search" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 260 }} />
          <button className="add-btn" style={{ background: '#6c757d', whiteSpace: 'nowrap' }} onClick={clearAllFilters}>Clear All Filters</button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="add-btn" onClick={fetchRows}>Refresh</button>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="add-btn" style={{ background: '#17a2b8' }} onClick={() => exportPDFSection(filteredRows, 'ZNZReport_All', { excludeColumns: ['duty_paid', 'duty_status', 'rcn_number', 'vehicle_no', 'border', 'border_point_confirmation'] })}>Download PDF</button>
            <button className="add-btn" style={{ background: '#007bff' }} onClick={() => exportExcelSection(filteredRows, 'ZNZReport_All', { excludeColumns: ['duty_paid', 'duty_status', 'rcn_number', 'vehicle_no', 'border', 'border_point_confirmation'] })}>Download Excel</button>
          </div>
        </div>
      </div>

      {!fixedRemarkFilter && (
        <>
          <div style={{ marginBottom: 26 }}>
            <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#d9ecff', borderRadius: 6, padding: 8, fontSize: 16 }}>
              ZNZ REPORT - ASSESSMENT ACCEPTED / SENT
            </div>
            {renderRows(acceptedRows)}
          </div>

          <div>
            <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#ffe3bf', borderRadius: 6, padding: 8, fontSize: 16 }}>
              PENDING ASSESSMENTS
            </div>
            {renderRows(pendingRows, { pendingSection: true })}
          </div>
        </>
      )}

      {fixedRemarkFilter && (
        <div>
          <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#c2fcc2', borderRadius: 6, padding: 8, fontSize: 16 }}>
            RELEASED REPORT
          </div>
          {renderRows(filteredRows, { readOnly: true })}
        </div>
      )}
    </div>
  );
};

export default function ZNZReport() {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('znzReport');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    if (!hasPermission('ZNZ REPORT', 'can_view')) {
      return;
    }
    if (!['znzReport', 'releasedReport'].includes(activeTab)) {
      setActiveTab('znzReport');
    }
  }, [activeTab, hasPermission]);

  if (!hasPermission('ZNZ REPORT', 'can_view')) {
    return (
      <div className="permission-denied-container" style={{ padding: '20px' }}>
        <h2>Permission Denied</h2>
        <p>You do not have permission to view ZNZ Reports.</p>
      </div>
    );
  }

  return (
    <div className="job-list-container" style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>ZNZ Reports</h2>
        <button onClick={handleRefresh} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
      </div>

      <div className="custom-tabs-container" style={{ marginBottom: 16 }}>
        <button className={`custom-tab ${activeTab === 'znzReport' ? 'active' : ''}`} onClick={() => { setActiveTab('znzReport'); handleRefresh(); }}>
          ZNZ Report
        </button>
        <button className={`custom-tab ${activeTab === 'releasedReport' ? 'active' : ''}`} onClick={() => { setActiveTab('releasedReport'); handleRefresh(); }}>
          Released Report
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'znzReport' && (
          <ZNZMainTab fixedRemarkFilter="" refreshKey={refreshKey} />
        )}
        {activeTab === 'releasedReport' && (
          <ZNZMainTab fixedRemarkFilter="RELEASED" refreshKey={refreshKey} />
        )}
      </div>
    </div>
  );
}
